import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Ticket } from "lucide-react";

/**
 * Embeds the official Ludus events widget inside an iframe, styled to match
 * the Encore Onyx + Bronze aesthetic.
 *
 * Ludus's widget.js uses legacy document.write(), so we host it inside a
 * srcDoc iframe to give it its own document context. The inner Ludus content
 * is cross-origin and can't be CSS-themed by us, but the wrapping presentation
 * is fully under our control.
 */
export default function LudusWidget({
  subdomain = "encorebham",
  showID = "",
  sections = "all", // 'all' | 'events' | 'collections' | 'classes'
  categoryID = "",
  hideNav = "false",
  width = "100%",
  initialHeight = 800,
}) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(initialHeight);

  const directLink = `https://${subdomain}.ludus.com`;

  // The srcDoc bg matches our page charcoal so any margin around the Ludus
  // inner-iframe blends seamlessly with the site.
  const srcDoc = useMemo(
    () => `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet" />
  <style>
    html, body {
      margin: 0;
      padding: 0;
      background: #372C2E;
      color: #FFFFFF;
      font-family: 'Montserrat', system-ui, -apple-system, sans-serif;
    }
    body { overflow-x: hidden; }
    a { color: #DE9E48; }
    iframe { background: transparent !important; }
  </style>
</head>
<body>
  <script type="text/javascript">
    var subdomain = ${JSON.stringify(subdomain)};
    var showID = ${JSON.stringify(showID)};
    var sections = ${JSON.stringify(sections)};
    var categoryID = ${JSON.stringify(categoryID)};
    var hideNav = ${JSON.stringify(hideNav)};
    var width = ${JSON.stringify(width)};
  </script>
  <script type="text/javascript" src="https://${subdomain}.ludus.com/widget.js"></script>
  <script>
    function reportHeight() {
      var h = document.documentElement.scrollHeight || document.body.scrollHeight;
      if (window.parent) {
        window.parent.postMessage({ source: 'ludus-widget', height: h }, '*');
      }
    }
    window.addEventListener('load', function () {
      reportHeight();
      setTimeout(reportHeight, 800);
      setTimeout(reportHeight, 2500);
    });
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(reportHeight).observe(document.body);
    }
  </script>
</body>
</html>`,
    [subdomain, showID, sections, categoryID, hideNav, width],
  );

  useEffect(() => {
    const onMessage = (e) => {
      const data = e?.data;
      if (data && data.source === "ludus-widget" && typeof data.height === "number") {
        setHeight(Math.max(initialHeight, data.height + 40));
      }
      // Ludus widget.js posts 'resize::NNN' strings.
      if (typeof data === "string" && data.indexOf("resize::") === 0) {
        const h = parseInt(data.replace("resize::", ""), 10);
        if (!Number.isNaN(h) && h > 100) {
          setHeight(Math.max(initialHeight, h + 40));
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [initialHeight]);

  return (
    <section
      data-testid="ludus-widget-section"
      className="py-28 md:py-32 px-6 md:px-10 relative overflow-hidden"
    >
      {/* Soft bronze halo accent */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full opacity-[0.06] blur-3xl"
        style={{ background: "radial-gradient(circle, #DE9E48 0%, transparent 70%)" }}
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Section header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="font-overline text-xs text-[#DE9E48] mb-4">— On Stage</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-none">
              Upcoming <span className="italic text-[#DE9E48]">Events.</span>
            </h2>
            <p className="font-body text-stone-400 mt-5 max-w-xl leading-relaxed">
              Browse and reserve seats for every show, class, and collection — powered by our
              ticketing partner.
            </p>
          </div>
          <a
            data-testid="ludus-fallback-link"
            href={directLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline inline-flex items-center gap-2 whitespace-nowrap"
          >
            <Ticket size={14} /> Full Schedule <ArrowRight size={14} />
          </a>
        </div>

        {/* Framed iframe — bronze hairline + charcoal bg matching site palette */}
        <div className="relative rounded-2xl border border-[#DE9E48]/30 bg-[#372C2E] shadow-[0_0_0_1px_rgba(222, 158, 72,0.1)_inset] overflow-hidden">
          {/* Top accent band */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#DE9E48]/20 bg-stone-950/30">
            <span className="font-overline text-[0.65rem] tracking-[0.22em] text-[#DE9E48]">
              — Live from Ludus
            </span>
            <span className="font-overline text-[0.6rem] tracking-[0.18em] text-stone-500">
              Secure ticketing · {subdomain}.ludus.com
            </span>
          </div>

          <iframe
            ref={iframeRef}
            data-testid="ludus-widget-iframe"
            title="Ludus Events"
            srcDoc={srcDoc}
            style={{ width: "100%", height: `${height}px`, border: 0, display: "block" }}
            loading="lazy"
          />
        </div>

        <p className="font-overline text-[0.65rem] text-stone-500 mt-5 text-center">
          Tickets, seating, and checkout are handled securely by Ludus.
        </p>
      </div>
    </section>
  );
}
